import React, { useEffect, useState, useCallback } from "react";
import { api, formatDate, formatMoney, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import { Truck, CheckCircle2, Phone, Filter, RefreshCw, Wallet } from "lucide-react";
import { toast } from "sonner";

export default function Despacho() {
  const [pedidos, setPedidos] = useState([]);
  const [entregadosHoy, setEntregadosHoy] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState("");
  const [includeDelivered, setIncludeDelivered] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { incluir_entregados: includeDelivered };
      if (fecha) params.fecha = fecha;
      const r = await api.get("/despacho/pedidos", { params });
      setPedidos(r.data);
      // Always fetch today's delivered total so the counter is meaningful
      const tparams = { incluir_entregados: true };
      if (fecha) tparams.fecha = fecha;
      const totals = await api.get("/despacho/pedidos", { params: tparams });
      setEntregadosHoy(totals.data.filter((p) => p.estado === "ENTREGADO").length);
    } finally { setLoading(false); }
  }, [fecha, includeDelivered]);

  useEffect(() => { load(); }, [load]);

  const marcarEntregado = async (p) => {
    try {
      await api.post(`/despacho/pedidos/${p.id}/entregar`);
      toast.success(`Entregado: ${p.cliente}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const pendientes = pedidos.filter((p) => p.estado === "CONFIRMADO");
  const entregados = pedidos.filter((p) => p.estado === "ENTREGADO");

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-1">Modo despacho</div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Pedidos para entregar</h1>
        </div>
        <div className="flex items-center gap-2">
          <DatePicker value={fecha} onChange={(v) => setFecha(v || "")} testid="desp-fecha" />
          <Button variant="outline" size="sm" className="border-[#E5E0D8]" onClick={load} disabled={loading} data-testid="desp-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <label className="flex items-center gap-2 text-sm text-[#595959] cursor-pointer">
          <input
            type="checkbox"
            checked={includeDelivered}
            onChange={(e) => setIncludeDelivered(e.target.checked)}
            className="h-4 w-4"
            data-testid="desp-include-delivered"
          />
          Mostrar también entregados
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <Card className="p-3 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Pendientes</div>
          <div className="font-display font-bold text-3xl text-[#C85A32]" data-testid="desp-count-pendientes">{pendientes.length}</div>
        </Card>
        <Card className="p-3 border-[#E5E0D8] rounded-2xl bg-white">
          <div className="text-[10px] uppercase tracking-wider text-[#8C8C8C]">Entregados {fecha ? "" : "hoy"}</div>
          <div className="font-display font-bold text-3xl text-[#3F6B3C]" data-testid="desp-count-entregados">{entregadosHoy}</div>
        </Card>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-[#8C8C8C] border-[#E5E0D8] rounded-2xl">Cargando...</Card>
      ) : pendientes.length === 0 && (!includeDelivered || entregados.length === 0) ? (
        <Card className="p-10 text-center border-[#E5E0D8] rounded-2xl bg-white">
          <Truck className="h-12 w-12 mx-auto text-[#C85A32] opacity-50 mb-3" />
          <div className="font-display font-semibold text-lg">No hay pedidos para entregar</div>
          <p className="text-sm text-[#595959] mt-1">Todos los pedidos confirmados ya fueron entregados.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {pendientes.map((p) => <PedidoCard key={p.id} pedido={p} onEntregado={marcarEntregado} />)}
          {includeDelivered && entregados.map((p) => <PedidoCard key={p.id} pedido={p} delivered />)}
        </div>
      )}
    </div>
  );
}

function PedidoCard({ pedido, onEntregado, delivered = false }) {
  const debe = (pedido.cuenta_corriente && pedido.monto_pagado < pedido.total);
  return (
    <Card
      className={`p-4 sm:p-5 border-[#E5E0D8] rounded-2xl bg-white ${delivered ? "opacity-60" : ""}`}
      data-testid={`desp-card-${pedido.pedido_id}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-bold text-[#C85A32]">{pedido.pedido_id}</span>
            {delivered && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border bg-[#DAE6D7] text-[#3F6B3C] border-[#BCD3B7]">
                Entregado
              </span>
            )}
            {debe && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] border bg-[#FDF3E1] text-[#A6751F] border-[#F0DEB2]">
                <Wallet className="h-3 w-3" /> Cobrar saldo
              </span>
            )}
          </div>
          <div className="font-display font-semibold text-lg mt-0.5">{pedido.cliente}</div>
          {pedido.telefono && (
            <a href={`tel:${pedido.telefono}`} className="inline-flex items-center gap-1 text-[12px] text-[#595959] hover:text-[#C85A32] mt-0.5">
              <Phone className="h-3 w-3" />{pedido.telefono}
            </a>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono font-bold text-xl">{formatMoney(pedido.total)}</div>
          {debe && (
            <div className="text-[11px] text-[#A6751F] font-mono">
              Saldo: {formatMoney(pedido.total - (pedido.monto_pagado || 0))}
            </div>
          )}
        </div>
      </div>

      <div className="text-[13px] text-[#595959] mb-3">
        <div className="font-semibold text-[#1A1A1A] text-[12px] uppercase tracking-wider mb-1">{pedido.items_count} unidades</div>
        <ul className="space-y-0.5">
          {pedido.items_resumen.map((line, i) => (
            <li key={i} className="font-mono text-[12px]">· {line}</li>
          ))}
        </ul>
      </div>

      {pedido.nota && (
        <div className="bg-[#FDF3E1] border border-[#F0DEB2] rounded-lg px-3 py-2 mb-3 text-[12px] text-[#A6751F]">
          📝 {pedido.nota}
        </div>
      )}

      {!delivered && onEntregado && (
        <Button
          className="w-full bg-[#3F6B3C] hover:bg-[#2F5A2C] text-white h-11 gap-2"
          onClick={() => onEntregado(pedido)}
          data-testid={`desp-entregar-${pedido.pedido_id}`}
        >
          <CheckCircle2 className="h-5 w-5" /> Marcar entregado
        </Button>
      )}
    </Card>
  );
}
